import {
    Button,
    FlexContainer,
    Input,
    Item
} from "@axxes/design-system";
import React, {ReactElement, useEffect, useReducer, useState} from "react";
import {APP_CONFIG} from "../../../app/config/config";
import EditOverlay from "../../../common/components/EditOverlay/EditOverlay";
import ItemForm from "../../../common/components/ItemForm/ItemForm";
import { ModalTypes } from "../../../common/models/modals";
import { useAppDispatch, useAppSelector } from "../../../common/store/hooks";
import { showModal } from "../../../common/store/slice";
import { sortByName } from "../../../common/utils/sorters";
import { truncate } from "../../../common/utils/strings";
import Avatar from "../../../users/components/Avatar/Avatar";
import AvatarTitle from "../../../users/components/Avatar/AvatarTitle";
import {School} from "../../../users/model";
import {fetchSchools} from "../../../users/store/usersFacadeService";
import {replaceSchool, updateSchool} from "../../store/manageFacadeService";
import '../edit.scss'
import editSchoolsReducer, {
    INITIAL_EDIT_SCHOOLS_STATE, resetSchool, setSchool,
} from "./editSchoolsState";

export const EditSchools = () => {

    const reduxDispatch = useAppDispatch();

    const [search, setSearch] = useState<string>('')

    const [editing, setEditing] = useState<string | null>(null)

    const [state, dispatch] = useReducer(
        editSchoolsReducer,
        INITIAL_EDIT_SCHOOLS_STATE,
    );

    const resetEditing = () => setEditing(null)

    const cancel = () => {
        resetEditing();
        dispatch(resetSchool());
    }

    const saveChanges = () => {
        if (state.editingSchool) reduxDispatch(updateSchool(state.editingSchool))
        cancel();
    }

    const schools = useAppSelector(
        (appState) => appState?.users?.schools?.fetch?.result || [],
    );

    const loadingSchools = useAppSelector(
        (appState) => appState?.users?.schools?.fetch?.isLoading,
    );

    const openModal = () => {
        reduxDispatch(
          showModal(ModalTypes.REPLACE, {
            label: 'school',
            onReplace: submitReplace,
            dropDownItems: schools.map(school => ({value: school.id || '', name: school.name || ''})),
            objToReplace: state?.editingSchool,
          }),
        );
    }

    const submitReplace = (replaceId: string) => {
      const newSchool = schools.find(s => s.id === replaceId);
      if (state.editingSchool && newSchool) {
        reduxDispatch(
          replaceSchool(state.editingSchool, newSchool),
        );
      }
      cancel();
    }

    useEffect(() => {
        reduxDispatch(fetchSchools())
    }, [])

    const mapIssuer = (school: School) => {
        return (
            <div className="axxes-edit__item" key={school?.id}>
                <EditOverlay
                    isEditable={editing === null}
                    setEdit={() => {
                        dispatch(setSchool(school));
                        setEditing(school?.id || null)
                    }}
                    edit={editing === school?.id}
                    viewElement={
                        <Avatar photo={school?.pictureUrl || APP_CONFIG.DEFAULT_IMAGE}>
                            <AvatarTitle>{truncate(school?.name || "", 32)}</AvatarTitle>
                        </Avatar>
                    }
                    editElement={
                        <div>
                            <ItemForm
                                newItem={state?.editingSchool}
                                open={editing === school?.id}
                                withName={true}
                                withPhoto={true}
                                title="Edit"
                                setNewItem={(value: School) => dispatch(setSchool(value))}
                            />
                            <FlexContainer
                                direction="row"
                                customClasses="axxes-edit__item-actions"
                            >
                                <Button
                                    disabled={false}
                                    accent={false}
                                    type="submit"
                                    isSaving={false}
                                    onClick={cancel}
                                    customClasses="axxes-edit__item-button"
                                >
                                    <span>Cancel</span>
                                </Button>
                                <Button
                                    disabled={false}
                                    accent={true}
                                    variant={"default"}
                                    type="submit"
                                    isSaving={false}
                                    onClick={openModal}
                                    customClasses="axxes-edit__item-button"
                                >
                                    <span>Replace</span>
                                </Button>
                                <Button
                                    disabled={false}
                                    accent={true}
                                    variant={"default"}
                                    type="submit"
                                    isSaving={false}
                                    onClick={saveChanges}
                                >
                                    <span>Save</span>
                                </Button>
                            </FlexContainer>
                        </div>
                    }
                />
            </div>
        )
    };

    const filterIssuer = (school: School) => {
        if (search === '') return true;
        return school?.name?.toLowerCase().includes(search);
    };
    const mappedSchools : ReactElement[] = schools.filter(filterIssuer).sort(sortByName).map(mapIssuer);
    return <>
        {loadingSchools ? <div>Loading Schools</div>
            : <>
                <div className="axxes-edit__search">
                    <Input title="Search Schools"
                           defaultValue={search}
                           onChange={((e) => setSearch(e.target.value.toLowerCase()))}
                    />
                </div>
                <div>Found {mappedSchools.length} schools: </div>
                <FlexContainer
                    padding="0"
                    customClasses="axxes-edit__container"
                >
                    {mappedSchools}
                </FlexContainer>
            </>}
    </>
}